// AppDateTimePicker.jsx
// App-standard date/time picker. A thin wrapper over MUI <DateTimePicker> that:
//   1. Opens the calendar/clock popup when the user clicks ANYWHERE in the field
//      (not just the small calendar icon) — the icon-only affordance isn't
//      obvious to users. Manual typing still works while the popup is open.
//   2. Bundles its own <LocalizationProvider> (AdapterMoment) so call-sites just
//      drop it in.
//   3. Lives inside MUI's own portal / z-index / focus system, so it works
//      correctly inside AppModal (MUI <Dialog>) — unlike react-flatpickr, whose
//      body-portaled calendar fought the Dialog focus trap + z-index.
//
// Value/onChange are plain MUI DateTimePicker props (moment in, moment out) — the
// call-site owns any conversion to/from the stored string format.
//
// Usage:
//   <AppDateTimePicker
//     value={stored ? moment(stored, "YYYY-MM-DD HH:mm:ss") : null}
//     onChange={(val) => save(val && val.isValid() ? format(val) : "")}
//     slotProps={{ textField: { placeholder: "Select date/time" } }}
//   />

import React, { useState } from "react";
import PropTypes from "prop-types";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

export default function AppDateTimePicker({ slotProps = {}, ...pickerProps }) {
  const [open, setOpen] = useState(false);
  const { textField, ...restSlotProps } = slotProps;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DateTimePicker
        // Match the familiar old flatpickr display ("14, Jul, 2026 08:46").
        // Placed before {...pickerProps} so a call-site can still override.
        format="DD, MMM, YYYY HH:mm"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        {...pickerProps}
        slotProps={{
          ...restSlotProps,
          textField: {
            fullWidth: true,
            size: "small",
            ...textField,
            // Open the picker on any click in the field, then run whatever
            // onClick the caller supplied. Typing still works while it's open.
            onClick: (e) => {
              setOpen(true);
              textField?.onClick?.(e);
            },
          },
        }}
      />
    </LocalizationProvider>
  );
}

AppDateTimePicker.propTypes = {
  // All MUI <DateTimePicker> props pass through (value, onChange, minDateTime…).
  slotProps: PropTypes.object,
};
